import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Share2, Heart, User, Link2, Check } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ShareNeedsModal = lazy(() =>
  import("./ShareNeedsModal").then((m) => ({ default: m.ShareNeedsModal }))
);
const ShareProfileModal = lazy(() =>
  import("./ShareProfileModal").then((m) => ({ default: m.ShareProfileModal }))
);
import { buildShelterSlug } from "@/lib/shelterSlug";
import type { Shelter } from "@/types/shelter";

const SITE = "https://gdzieschronisko.pl";

interface ShareChoiceDialogProps {
  shelter: Shelter;
  variant?: "button" | "icon" | "subtle";
}

export const ShareChoiceDialog = ({ shelter, variant = "icon" }: ShareChoiceDialogProps) => {
  const [isChoiceOpen, setIsChoiceOpen] = useState(false);
  const [shareType, setShareType] = useState<"needs" | "profile" | null>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [isTouchUi, setIsTouchUi] = useState(false);
  const [copied, setCopied] = useState(false);

  const shelterUrl = `${SITE}/schronisko/${buildShelterSlug(shelter)}?utm_source=share`;

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const copyViaTextarea = (text: string): boolean => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch {
        return false;
      }
    };

    let success = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shelterUrl);
        success = true;
      }
    } catch {
      // ignore – spróbujemy fallbacku
    }
    if (!success) success = copyViaTextarea(shelterUrl);

    if (success) {
      setCopied(true);
      toast.success("Link skopiowany");
      trackEvent("Udostępnij - skopiowano link", shelterFields(shelter));
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Nie udało się skopiować linku", {
        description: "Twoja przeglądarka zablokowała dostęp do schowka. Zaznacz adres w polu obok i skopiuj go ręcznie (Ctrl/Cmd + C).",
        duration: 6000,
      });
    }
  };

  useEffect(() => {
    // Na urządzeniach dotykowych iOS Safari potrafi zostawić „sticky focus/hover”,
    // co wygląda jak zaznaczenie pierwszej opcji.
    setIsTouchUi(window.matchMedia?.("(hover: none)")?.matches ?? false);
  }, []);

  useEffect(() => {
    if (!isChoiceOpen) return;
    // iOS/Safari potrafi ustawić focus na pierwszym przycisku w dialogu,
    // co wygląda jak "zaznaczenie". Przenosimy focus na kontener dialogu.
    requestAnimationFrame(() => {
      dialogContentRef.current?.focus();
    });
  }, [isChoiceOpen]);

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent("Udostępnij - otwarto", shelterFields(shelter));
    setIsChoiceOpen(true);
  };

  const handleChooseNeeds = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent("Udostępnij potrzeby", shelterFields(shelter));
    setIsChoiceOpen(false);
    setShareType("needs");
  };

  const handleChooseProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent("Udostępnij profil", shelterFields(shelter));
    setIsChoiceOpen(false);
    setShareType("profile");
  };

  return (
    <>
      {variant === "icon" ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleIconClick}
                className="p-1.5 rounded-full transition-colors text-muted-foreground hover:text-primary hover:bg-primary/10 [-webkit-tap-highlight-color:transparent]"
                aria-label="Udostępnij"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Udostępnij</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : variant === "subtle" ? (
        <button
          onClick={handleIconClick}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          <Share2 className="w-4 h-4" />
          Udostępnij
        </button>
      ) : (
        <Button
          variant="outline"
          onClick={() => { trackEvent("Udostępnij - otwarto", shelterFields(shelter)); setIsChoiceOpen(true); }}
          className="gap-1.5 w-full h-auto px-3 py-2 text-sm font-medium rounded-lg bg-card text-foreground border-border hover:bg-muted"
        >
          <Share2 className="w-4 h-4" />
          Udostępnij
        </Button>
      )}

      {/* Choice Dialog */}
      <Dialog open={isChoiceOpen} onOpenChange={setIsChoiceOpen}>
        <DialogContent
          ref={dialogContentRef}
          tabIndex={-1}
          className="max-w-sm outline-none max-h-[90dvh] overflow-y-auto p-4 sm:p-6 gap-3"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            dialogContentRef.current?.focus();
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Co chcesz udostępnić?
            </DialogTitle>
            <DialogDescription>
              Wybierz rodzaj grafiki do udostępnienia.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-2">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-background hover:text-foreground sm:hover:bg-accent sm:hover:text-accent-foreground active:bg-background [-webkit-tap-highlight-color:transparent]"
              onFocus={(e) => {
                if (isTouchUi) e.currentTarget.blur();
              }}
              onClick={handleChooseNeeds}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Aktualne potrzeby</p>
                <p className="text-xs text-muted-foreground">
                  Lista rzeczy, których schronisko potrzebuje
                </p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-background hover:text-foreground sm:hover:bg-accent sm:hover:text-accent-foreground active:bg-background [-webkit-tap-highlight-color:transparent]"
              onFocus={(e) => {
                if (isTouchUi) e.currentTarget.blur();
              }}
              onClick={handleChooseProfile}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Profil schroniska</p>
                <p className="text-xs text-muted-foreground">
                  Informacje kontaktowe, wolontariat, opis
                </p>
              </div>
            </Button>
          </div>

          <div className="border-t pt-3 mt-1">
            <p className="text-xs text-muted-foreground mb-2">
              Albo skopiuj link do profilu:
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
              <input
                type="text"
                readOnly
                inputMode="none"
                value={shelterUrl.replace(/^https?:\/\//, "")}
                onClick={(e) => {
                  const el = e.currentTarget;
                  el.setSelectionRange(0, el.value.length);
                }}
                onFocus={(e) => {
                  const el = e.currentTarget;
                  requestAnimationFrame(() => el.setSelectionRange(0, el.value.length));
                }}
                onTouchEnd={(e) => {
                  const el = e.currentTarget;
                  requestAnimationFrame(() => el.setSelectionRange(0, el.value.length));
                }}
                className="w-full sm:flex-1 min-w-0 px-3 py-2 rounded-md bg-muted text-xs text-muted-foreground font-mono border-0 focus:outline-none focus:ring-1 focus:ring-primary/30 cursor-text select-all truncate"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="w-full sm:w-auto shrink-0 gap-1.5"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                {copied ? "Skopiowano" : "Kopiuj link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Needs Modal - controlled mode (lazy) */}
      {shareType === "needs" && (
        <Suspense fallback={null}>
          <ShareNeedsModal
            shelter={shelter}
            controlledOpen={true}
            onControlledOpenChange={(open) => !open && setShareType(null)}
          />
        </Suspense>
      )}

      {/* Profile Modal (lazy) */}
      {shareType === "profile" && (
        <Suspense fallback={null}>
          <ShareProfileModal
            shelter={shelter}
            isOpen={true}
            onOpenChange={(open) => !open && setShareType(null)}
          />
        </Suspense>
      )}
    </>
  );
};
